import { useEffect, useRef, useState } from "react";

export interface AgendaItem {
  title: string;
  /** Duration per agenda item in seconds */
  duration: number;
}

interface AgendaSidebarProps {
  items: AgendaItem[];
  educatorMode?: boolean;
}

/**
 * AgendaSidebar — displays a vertical list of agenda items and visually fills
 * the current item to indicate elapsed time.  When the elapsed duration meets
 * the item’s total, the agenda automatically advances to the next entry.
 */
export default function AgendaSidebar({
  items,
  educatorMode = false,
}: AgendaSidebarProps) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0); // seconds within current item

  const runningRef = useRef(true);
  const elapsedRef = useRef(elapsed);
  const idxRef = useRef(currentIdx);

  useEffect(() => {
    elapsedRef.current = elapsed;
  }, [elapsed]);

  useEffect(() => {
    idxRef.current = currentIdx;
  }, [currentIdx]);

  // ticker responsible for updating progress
  useEffect(() => {
    const id = setInterval(() => {
      if (!runningRef.current) return;
      setElapsed((prev) => {
        const next = prev + 1;
        const currentItem = items[idxRef.current];
        if (!currentItem) return 0;
        if (next >= currentItem.duration) {
          // move to next agenda item
          if (idxRef.current < items.length - 1) {
            setCurrentIdx(idxRef.current + 1);
            return 0;
          }
          // reached end — stop
          runningRef.current = false;
          return currentItem.duration;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [items]);

  const setCurrent = (idx: number) => {
    setCurrentIdx(idx);
    setElapsed(0);
  };

  const currentItem = items[currentIdx];

  return (
    <div className="space-y-2">
      {items.map((item, idx) => {
        const progress = (() => {
          if (idx < currentIdx) return 1;
          if (idx === currentIdx && item.duration > 0) {
            return Math.min(elapsed / item.duration, 1);
          }
          return 0;
        })();

        const isCurrent = idx === currentIdx;

        return (
          <div
            key={item.title + idx}
            className={
              "relative overflow-hidden rounded-md border border-gray-300 px-3 py-2 text-sm font-medium dark:border-gray-600 " +
              (isCurrent ? "text-primary" : "text-gray-700 dark:text-gray-300")
            }
          >
            {/* progress fill */}
            <div
              className="absolute inset-0 -z-10 bg-primary/20 transition-all"
              style={{ width: `${progress * 100}%` }}
            />
            <div className="flex items-center justify-between">
              <span>{item.title}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {Math.ceil(item.duration / 60)}m
              </span>
            </div>

            {educatorMode && (
              <button
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                onClick={() => setCurrent(idx)}
              >
                <span className="sr-only">Select agenda item</span>
              </button>
            )}
          </div>
        );
      })}

      {/* status */}
      {currentItem && (
        <p className="pt-2 text-center text-xs text-gray-500 dark:text-gray-400">
          Current: {currentItem.title} — {Math.floor(elapsed / 60)}:{
            ("0" + (elapsed % 60)).slice(-2)
          } / {Math.floor(currentItem.duration / 60)}:{
            ("0" + (currentItem.duration % 60)).slice(-2)
          }
        </p>
      )}
    </div>
  );
}
